import React, { useMemo, useState } from "react";
import { FileText, FileSpreadsheet, FileType2, Link2, Download, ExternalLink, Search, FolderOpen, ClipboardCheck } from "lucide-react";
import { Panel, EmptyState } from "./ui/Layout";
import { Segmented } from "./ui/Tabs";
import { Button } from "./ui/Button";
import { fmtDateLong } from "./format";
import { DocReviewBadge, DocReviewModal, docReviewState } from "./DocReview";
import { DOC_CATS, docCategoryLabel, isDocLink, openDocument } from "./docs";

export { DOC_CATS, docCategoryLabel, isDocLink, openDocument };
const ICON = { pdf: FileText, docx: FileType2, xlsx: FileSpreadsheet, link: Link2 };

/** Filtre (categorii + căutare) + tabel documente — folosit în proiect și în pagina cross-proiect. */
export function DocumentsTable({ documents, showProject = false, extraFilter, testid = "docs-list", onChanged }) {
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [review, setReview] = useState(null);

  const counts = useMemo(() => { const c = { all: documents.length }; documents.forEach((d) => { c[d.category] = (c[d.category] || 0) + 1; }); return c; }, [documents]);
  const list = documents.filter((d) => (cat === "all" || d.category === cat) && (!q || `${d.name} ${d.project_name || ""}`.toLowerCase().includes(q.toLowerCase())));
  const toReview = documents.filter((d) => docReviewState(d)?.actionable).length;

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <Segmented ariaLabel="Categorie documente" value={cat} onChange={setCat}
          options={DOC_CATS.filter(([k]) => k === "all" || counts[k]).map(([k, l]) => ({ value: k, label: l, count: counts[k] || 0 }))} />
        <div className="flex items-center gap-3 lg:ml-auto">
          {extraFilter}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-text-3 pointer-events-none" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută document..." aria-label="Caută document" className="cp-input !h-9 !pl-9" data-testid="docs-search" />
          </div>
        </div>
      </div>
      {toReview > 0 && (
        <div className="cp-panel-amber px-4 py-2.5 mb-4 flex items-center gap-3 text-[13px] text-cp-text" data-testid="docs-review-banner">
          <ClipboardCheck className="h-4 w-4 text-cp-amber shrink-0" aria-hidden="true" />
          <span>{toReview === 1 ? "Un document așteaptă" : `${toReview} documente așteaptă`} revizuirea sau aprobarea ta. Apasă „Analizează” pe rândul documentului.</span>
        </div>
      )}
      {list.length === 0 ? <EmptyState compact icon={FolderOpen} title="Niciun document în această categorie" description="Schimbă filtrul sau șterge căutarea." /> : (
        <Panel padded={false} className="overflow-x-auto" data-testid={testid}>
          <table className="cp-table min-w-[820px]">
            <thead>
              <tr><th>Document</th>{showProject && <th>Proiect</th>}<th>Categorie</th><th>Dată publicare</th><th>Tip</th><th>Status</th><th className="text-right">Acțiuni</th></tr>
            </thead>
            <tbody>
              {list.map((d) => {
                const link = isDocLink(d);
                const Icon = ICON[link ? "link" : d.file_type] || FileText;
                const st = docReviewState(d);
                return (
                  <tr key={d.id} data-testid={`doc-${d.id}`}>
                    <td>
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className="h-[18px] w-[18px] text-cp-accent shrink-0" aria-hidden="true" />
                        <button type="button" onClick={() => openDocument(d, setBusy)} className="text-[14px] font-medium text-cp-accent hover:underline underline-offset-4 text-left truncate max-w-[360px]" data-testid={`doc-name-${d.id}`}>{d.name}</button>
                      </div>
                      {d.note && <div className="cp-meta mt-0.5 pl-[30px] truncate max-w-[380px]">{d.note}</div>}
                    </td>
                    {showProject && <td className="text-cp-text">{d.project_name}</td>}
                    <td>{docCategoryLabel(d.category)}</td>
                    <td className="whitespace-nowrap">{fmtDateLong(d.uploaded_at)}</td>
                    <td className="whitespace-nowrap">{link ? "Link extern" : (d.file_type || "Fișier").toUpperCase()}</td>
                    <td className="whitespace-nowrap">{st ? <DocReviewBadge doc={d} /> : <span className="cp-meta">—</span>}</td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-2">
                        {st?.actionable && <Button variant="amber" size="sm" onClick={() => setReview(d)} data-testid={`review-${d.id}`}>Analizează</Button>}
                        <Button variant="outline" size="sm" onClick={() => openDocument(d, setBusy)} loading={busy === d.id} data-testid={`download-${d.id}`} aria-label={`${link ? "Deschide" : "Descarcă"} ${d.name}`}>
                          {link ? <><ExternalLink /> Deschide</> : <><Download /> Descarcă</>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}
      <DocReviewModal doc={review} open={!!review} onOpenChange={(v) => !v && setReview(null)} onDone={onChanged} />
    </div>
  );
}
